import {
	REF_DELIMITER,
	deflateSharedPartCompId,
	deflateMultiSharedPartCompIds,
	inflateSharedPartCompId,
	inflateMultiSharedPartCompIds,
	getInflatedIdsKeys,
	hasFakeInflation,
} from '@wix/thunderbolt-commons'
import type { CssOverrides } from '@wix/thunderbolt-commons'
import type { Callback, ComponentWithInnerElement } from '@wix/viewer-manager-interface'

type DetachContainerOptions = {
	containerOverrides?: CssOverrides
	childrenOverrides?: { [compId: string]: CssOverrides }
}

function deflateMultiIdArguments(originalFn: Function): Function {
	return (compIds: Array<string>) => originalFn(deflateMultiSharedPartCompIds(compIds))
}

function deflateFirstArgument(originalFn: Function): Function {
	return (...args: Array<any>) => {
		const [compId, ...rest] = args

		return originalFn(deflateSharedPartCompId(compId), ...rest)
	}
}

function deflateCompIdInsideObjectArgument(originalFn: Function): Function {
	return (argsObj: any) => {
		return originalFn({ ...argsObj, compId: deflateSharedPartCompId(argsObj.compId) })
	}
}

function inflateResArray(originalFn: Function): Function {
	return (...args: Array<any>) => {
		const deflatedIds = originalFn(...args)

		return deflatedIds.map((deflatedId: string) => inflateSharedPartCompId(deflatedId))
	}
}

function interceptGetStructure(originalFn: Function): Function {
	return (compId: string) => {
		const deflatedId = deflateSharedPartCompId(compId)
		const isInputInflated = compId !== deflatedId

		const componentStructure = originalFn(deflatedId, isInputInflated)
		if (componentStructure) {
			// converting a comp to custom-global sets it as direct child of masterPage,
			// ...but we need to treat it as an inflated child of a relevant refComponent
			const isNotReparentedYet = componentStructure.parent === 'masterPage' && isInputInflated
			if (isNotReparentedYet) {
				componentStructure.parent = compId.split(`${REF_DELIMITER}${deflatedId}`)[0]
			}

			return {
				...componentStructure,
				id: compId,
				parent: isInputInflated
					? inflateSharedPartCompId(componentStructure.parent)
					: componentStructure.parent,
				components: componentStructure.components?.map((childId: string) => inflateSharedPartCompId(childId)),
			}
		}
	}
}

const nodeIdGetter = {
	get(target: any, prop: any) {
		if (prop === 'id') {
			return target.getAttribute('data-inflated-id') || target.id
		}

		return target[prop]
	},
}

// if proceed with individual validations for all the methods, move to thunderbolt-types
type PreviewTransitionMethod = (
	srcIds: Array<string>,
	targetIds: Array<string>,
	pageId: string,
	animationDef: string,
	onComplete: Callback
) => string | null

export const NO_INTERCEPTOR_SET = new Set([
	'viewMode.isDuringViewModeSwitch',
	'viewMode.isMobileView',
	'viewMode.registerToNextSwitchViewModeDone',
	'viewMode.registerToSwitchViewModeDone',
	'viewMode.waitForViewModeChange',
	'actions.runInBatch',
	'actionsAndBehaviors.disableAction',
	'actionsAndBehaviors.enableAction',
	'actionsAndBehaviors.executeAction',
	'actionsAndBehaviors.resetBehaviorsRegistration',
	'animation.getSiteAnimationsService',
	'animation.previewScrubAnimations',
	'animation.reloadPageAnimations',
	'animation.stopAndClearAllAnimations',
	'animation.stopPreviewAnimation',
	'animation.stopPreviewScrubAnimations',
	'animation.updateScrubAnimationsPreview',
	'blocks.getBlocksPreviewData',
	'blocks.setBlocksPreviewData',
	'byRef.getData', // id given here is dataQueryId, not compId
	'byRef.getRemoteOverrides',
	'canvas.cancelAll',
	'customCSS.refresh',
	'customElements.getCacheKillerCounter',
	'customElements.getCacheKillerMap',
	'customElements.setCacheKillerCounter',
	'customElements.setCacheKillerMap',
	'data.getData', // id given here is dataQueryId, not compId
	'data.updateData',
	'diagnostics.getDebugInfo',
	'documentServicesModel.getCustomHeadTags',
	'documentServicesModel.getIsPublished',
	'documentServicesModel.getPublicUrl',
	'documentServicesModel.getRevision',
	'documentServicesModel.getSiteName',
	'documentServicesModel.getVersion',
	'documentServicesModel.updateCustomHeadTags',
	'documentServicesModel.updateIsPublished',
	'documentServicesModel.updatePublicUrl',
	'documentServicesModel.updateRevision',
	'documentServicesModel.updateSiteName',
	'documentServicesModel.updateVersion',
	'externalComponents.getExternalComponentConsent',
	'externalComponents.setExternalComponentConsent',
	'fonts.getCompsWaitingForFonts',
	'fonts.getFontsCssUrls',
	'fonts.getFontsMetaData',
	'fonts.hasPendingFonts',
	'layout.clearCache',
	'layout.clearDetachedBasicMeasure',
	'layout.getInnerElementBoundingBoxes',
	'layout.getContainerBoundingBoxes',
	'layout.registerToComponentsLayoutChange',
	'layout.registerToMeasurementsChange',
	'layout.getAllMeasurements',
	'layout.registerToNextRenderDone',
	'layout.registerToRenderDone',
	'layout.getLayoutMechanism',
	'layout.getScreenHeight',
	'layout.getScreenSize',
	'layout.getScreenWidth',
	'layout.getSiteMeasureMap', // used only in Classic
	'layout.getSiteWidth',
	'layout.getSiteX',
	'layout.isLayoutPending',
	'livePreview.getAllowedApps',
	'livePreview.getLivePreviewEnabledFlag',
	'livePreview.isLivePreviewOpen',
	'livePreview.refreshAppsInCurrentPage',
	'livePreview.toggleLivePreviewEnabled',
	'members.getMemberDetailsInPreview',
	'members.hideAuthDialog',
	'members.showLoginDialog',
	'members.showSignUpDialog',
	'modes.getAllActiveModeIds',
	'modes.getAllActiveModes',
	'modes.resetAllActiveModes',
	'modes.switchModes',
	'modes.updateActiveSOSPModes',
	'multiLingual.getCurrentLanguage',
	'multiLingual.setCurrentLanguage',
	'navigation.getCurrentUrl',
	'navigation.getRootNavigationInfo',
	'navigation.isDuringNavigationInteraction',
	'navigation.registerToDynamicPagesNavigationError',
	'navigation.setPreviewTooltipCallback',
	'navigation.waitForNavigation',
	'pages.getAllRenderedRootIds',
	'pages.getCurrentPopupId',
	'pages.getCurrentUrlPageId',
	'pages.getFocusedRootId',
	'pages.getPrimaryPageId',
	'pages.getViewerLoadedPagesIds',
	'pages.isPopupOpened',
	'pathBuilder.buildPaths', // this api will be removed soon + ids set by user, not ours (by Eden)
	'platform.getComponentsByPageIdForWixCode',
	'platform.registerToAppInstanceUpdate',
	'platform.registerToNotifyApplicationRequestFromViewerWorker',
	'platformDsModel.getPlatformDsModel',
	'platformDsModel.updatePlatformDsModel',
	'query.getQueryParam',
	'query.isQaMode',
	'renderFlags.getRenderFlags',
	'rendererModel.getClientSpecMap', // not relevant: debugApi.dsCarmi.$model.clientSpecMap
	'rendererModel.getMetaSiteId',
	'rendererModel.getPagesPlatformApplications',
	'rendererModel.getPasswordProtectedPages',
	'rendererModel.getPremiumFeatures',
	'rendererModel.getRouters',
	'rendererModel.getSiteInfo',
	'rendererModel.getSiteMetaData',
	'rendererModel.getSitePropertiesInfo',
	'rendererModel.getUrlMappings',
	'rendererModel.getWixCodeModel',
	'rendererModel.updateBlocksExperiments',
	'rendererModel.updateClientSpecMap',
	'rendererModel.updateMetaSiteId',
	'rendererModel.updatePagesPlatformApplications',
	'rendererModel.updatePasswordProtectedPages',
	'rendererModel.updatePlatformAppsExperiments',
	'rendererModel.updatePremiumFeatures',
	'rendererModel.updateRouters',
	'rendererModel.updateSiteInfo',
	'rendererModel.updateSiteMetaData',
	'rendererModel.updateSitePropertiesInfo',
	'rendererModel.updateUrlMappings',
	'rendererModel.updateWixCodeModel',
	'responsive.clearDetached',
	'responsive.getScrollBarWidth',
	'responsive.clearCompCssOverrides',
	'runtime.getWantedIsMobileView',
	'runtime.getWantedNavInfo',
	'runtime.refreshPage',
	'runtime.setPagesModelsFetcher',
	'runtime.setWantedIsMobileView',
	'runtime.setWantedNavInfo',
	'runtime.setGlobalModelsFetcher',
	'runtimeLayout.clearDetached', // used only in classic editor
	'runtimeLayout.detach', // used only in classic editor
	'runtimeLayout.detachMulti', // used only in classic editor
	'runtimeLayout.getRuntimeProperty', // used only in classic editor
	'runtimeLayout.updateDetached', // used only in classic editor
	'scroll.animateScroll',
	'scroll.getScroll',
	'scroll.isSiteScrollingBlocked',
	'scroll.scrollToAnchor',
	'scroll.setOnComponentsScrollListener',
	'scroll.setScroll',
	'scroll.setScrollAndScale',
	'stylable.getStylableEditorInstance',
	'stylable.mergeStylesheets',
	'svg.addSvgShape',
	'svg.getSvgShape',
	'tpa.getModalCompData',
	'tpa.getPopupsCompData',
	'tpa.getSiteMap',
	'tpa.removeAllPopups',
	'tpa.removeModal',
	'updateStatus.waitForDataRequirements',
	'updateStatus.waitForViewer',
	'variants.setEnableVariantsTransitionsInEditor',
	'variants.setGlobalIgnoredTransformProps',
	'variants.setTypeIgnoredTransformProps',
	'velo.refreshFilesOrFolders',
	'motion.cancel',
	'motion.cancelAll',
	'motion.cancelScrub',
	'motion.disable',
	'motion.enable',
	'motion.startScrub',
	'motion.updateScrub',
])

export const INFLATED_COMP_ID_INTERCEPTORS: { [key: string]: Function } = {
	'animation.previewAnimation': deflateFirstArgument,
	'animation.previewTransition': (originalFn: PreviewTransitionMethod): Function => {
		return (
			srcIds: Array<string>,
			targetIds: Array<string>,
			pageId: string,
			animationDef: string,
			onComplete: Callback
		) => {
			return originalFn(
				deflateMultiSharedPartCompIds(srcIds),
				deflateMultiSharedPartCompIds(targetIds),
				pageId,
				animationDef,
				onComplete
			)
		}
	},
	'byRef.getDisplayedConnectionOverrides': deflateFirstArgument,
	'byRef.getHiddenCompsGroupedByRefComponent': (originalFn: Function): Function => {
		return (refId: string) => {
			const res = originalFn(deflateSharedPartCompId(refId))

			return inflateMultiSharedPartCompIds(res)
		}
	},
	'byRef.getInternallyReferredComps': (): Function => {
		return (refId: string) => {
			return getInflatedIdsKeys(refId)
		}
	},
	'byRef.getStructure': interceptGetStructure,
	'canvas.play': deflateFirstArgument,
	'canvas.cancel': deflateFirstArgument,
	'canvas.update': deflateFirstArgument,
	'canvas.addCustomEffect': deflateFirstArgument,
	'canvas.updateCustomEffect': deflateFirstArgument,
	'canvas.removeCustomEffects': deflateFirstArgument,
	'component.getElementComputedStyle': (originalFn: Function): Function => {
		return (path: Array<string>, propKeys: Array<string>) => {
			return originalFn(deflateMultiSharedPartCompIds(path), propKeys)
		}
	},
	'component.isComponentRenderedOnSite': deflateFirstArgument,
	'componentDetector.getElementsUnderXY': (originalFn: Function): Function => {
		return (...args: Array<any>) => {
			const elements = originalFn(...args)

			return elements.map((it: any) => new Proxy(it, nodeIdGetter))
		}
	},
	'componentDetector.getComponentsUnderXY': inflateResArray,
	'componentDetector.getInnerElementsUnderXY': (originalFn: Function): Function => {
		return (...args: Array<any>) => {
			const { visibleElements, hiddenByBlockingLayer } = originalFn(...args)

			function inflateComps(comps: Array<ComponentWithInnerElement>) {
				return comps.map((comp: any) => ({
					compId: inflateSharedPartCompId(comp.compId),
					innerElementPath: comp.innerElementPath,
				}))
			}

			return {
				visibleElements: inflateComps(visibleElements),
				hiddenByBlockingLayer: inflateComps(hiddenByBlockingLayer),
			}
		}
	},
	'data.getCompIdByDataQueryId': (originalFn: Function): Function => {
		return (dataQueryId: string, pageId?: string) => {
			// if template component is requested, omit page argument - otherwise it's filtered out by page check in original method
			// (compIdToPageIdMap[compId] returns current page, as masterPage is overridden in model.structure metaData)
			const compIdFromDataQuery = dataQueryId.split('-dataQuery')[0]
			const isTemplateId = hasFakeInflation(compIdFromDataQuery) && pageId === 'masterPage'

			return isTemplateId ? originalFn(dataQueryId) : inflateSharedPartCompId(originalFn(dataQueryId, pageId))
		}
	},
	'data.getTranslationDataItem': deflateFirstArgument,
	'data.updateTranslationDataItem': deflateFirstArgument,
	'displayedOnlyComponents.getRepeatedComponents': deflateFirstArgument,
	'displayedOnlyComponents.getRepeaterToTemplateItem': deflateFirstArgument,
	'displayedOnlyComponents.setRepeaterToTemplateItem': deflateFirstArgument,
	'layout.getBasicMeasureForComp': deflateFirstArgument,
	'layout.getBasicMeasureForCompWithoutTransform': deflateFirstArgument,
	'layout.getComponentBoundingBox': deflateFirstArgument,
	'layout.getComponentInnerElementBoundingBoxes': deflateFirstArgument,
	'layout.getContentArea': deflateFirstArgument,
	'layout.getPadding': deflateFirstArgument,
	'layout.getOriginalComponentBoundingBox': deflateFirstArgument,
	'layout.getRelativeToViewportBoundingBox': deflateFirstArgument,
	'layout.isShowOnFixedPosition': deflateFirstArgument,
	'layout.keepDetachedBasicMeasure': deflateMultiIdArguments,
	'layout.updateAndPushEnd': deflateFirstArgument,
	'layout.updateAndPushStart': deflateFirstArgument,
	'layout.updateAndPushUpdate': deflateFirstArgument,
	'livePreview.triggerOnAppSettingsUpdate': deflateCompIdInsideObjectArgument,
	'livePreview.triggerWidgetStateUpdate': deflateCompIdInsideObjectArgument,
	'livePreview.triggerWidgetStateUpdateAfterRefresh': deflateCompIdInsideObjectArgument,
	'modes.activateMode': deflateFirstArgument,
	'modes.deactivateMode': deflateFirstArgument,
	'platform.getRuntimeDal': (originalFn: Function): Function => {
		return () => {
			return {
				getCompState: deflateFirstArgument(originalFn),
				hasRuntimeChanges: deflateFirstArgument(originalFn),
				getCompData: deflateFirstArgument(originalFn),
			}
		}
	},
	'platform.triggerAppStudioWidgetOnPropsChanged': (originalFn: Function): Function => {
		return (pageId: string, widgetId: string, newProps: any) => {
			return originalFn(pageId, deflateSharedPartCompId(widgetId), newProps)
		}
	},
	'realTimeConfig.getCompsToShowOnTop': inflateResArray,
	'realTimeConfig.getCompsToShowWithOpacity': inflateResArray,
	'realTimeConfig.getHiddenTextComponent': (originalFn: Function): Function => {
		return () => inflateSharedPartCompId(originalFn())
	},
	'realTimeConfig.getIsCompHidden': deflateFirstArgument,
	'realTimeConfig.setCompsToShowOnTop': deflateMultiIdArguments,
	'realTimeConfig.setCompsToShowWithOpacity': (originalFn: Function): Function => {
		return (obj: any) => {
			return originalFn({
				...obj,
				compIds: obj.compIds.map((inflatedId: string) => deflateSharedPartCompId(inflatedId)),
			})
		}
	},
	'realTimeConfig.setHiddenComp': deflateFirstArgument,
	'realTimeConfig.setHideTextComponent': deflateFirstArgument,
	'responsive.detach': deflateFirstArgument,
	'responsive.detachContainer': (originalFn: Function): Function => {
		return (compId: string, options: DetachContainerOptions = {}) => {
			const deflatedCompId = deflateSharedPartCompId(compId)
			const deflatedChildrenOverrides: { [compId: string]: CssOverrides } = {}

			if (options.childrenOverrides) {
				Object.entries(options.childrenOverrides).forEach(([childId, childOverrides]) => {
					deflatedChildrenOverrides[deflateSharedPartCompId(childId)] = childOverrides
				})
			}

			return originalFn(deflatedCompId, { ...options, childrenOverrides: deflatedChildrenOverrides })
		}
	},
	'responsive.detachMulti': deflateMultiIdArguments,
	'responsive.getClientHeight': deflateFirstArgument,
	'responsive.getClientWidth': deflateFirstArgument,
	'responsive.getGridMeasures': deflateFirstArgument,
	'responsive.getScrollHeight': deflateFirstArgument,
	'responsive.getScrollWidth': deflateFirstArgument,
	'responsive.updateDetached': deflateFirstArgument,
	'responsive.updateDetachedRotation': deflateFirstArgument,
	'responsive.updateDetachedStyles': deflateFirstArgument,
	'responsive.updateDetachedTransformation': deflateFirstArgument,
	'responsive.updateGridCssOverrides': deflateFirstArgument,
	'responsive.updatePaddingCssOverrides': deflateFirstArgument,
	'scopes.getRepeaterItemsIndexes': deflateFirstArgument,
	'scopes.getScope': (originalFn: Function): Function => {
		return (id: string) => {
			const deflatedId = deflateSharedPartCompId(id)
			const inflatedId = inflateSharedPartCompId(id)

			// if requested id was inflated, we want to deflate it to reach it in DOM
			const idWasInflated = deflatedId !== id
			if (idWasInflated) {
				return originalFn(deflatedId)
			}

			// if requested id was non-inflated, but would be inflated without experiment,
			// we don't expect it in DOM, don't expect any scope, so return []
			const idIsNotExpectedInDom = inflatedId !== id
			if (idIsNotExpectedInDom) {
				return []
			}

			// if requested id was non-inflated & cannot be inflated, OR inflated & cannot be deflated,
			// so we expect it in DOM and use original flow
			return originalFn(id)
		}
	},
	'scroll.getTotalScroll': deflateFirstArgument,
	'scroll.scroll': deflateFirstArgument,
	'scroll.scrollBy': deflateFirstArgument,
	'scroll.scrollToComponent': deflateFirstArgument,
	'scroll.isScrollable': deflateFirstArgument,
	'structure.getStructure': interceptGetStructure,
	'structure.updateStructure': deflateFirstArgument,
	'stylable.forceState': deflateFirstArgument,
	'stylable.revertForceState': deflateFirstArgument,
	'stylable.revertQuickChanges': deflateFirstArgument,
	'stylable.setQuickChanges': deflateFirstArgument,
	'tpa.getCurrentTpaIds': inflateResArray,
	'tpa.getTpaCompPreviewData': deflateFirstArgument,
	'tpa.handleDsTpaHandler': (originalFn: Function): Function => {
		return ({ msg, addCallback, callback }: any) => {
			return originalFn({ msg: { ...msg, compId: deflateSharedPartCompId(msg.compId) }, addCallback, callback })
		}
	},
	'tpa.setCompState': deflateFirstArgument,
	'tpa.setTpaCompPreviewData': deflateFirstArgument,
	'variants.getActiveVariant': deflateFirstArgument,
	'variants.setCompsIgnoredTransformProps': (originalFn: Function): Function => {
		return (...args: Array<any>) => {
			const [compIds, ...rest] = args
			const deflatedIds = compIds.map((inflatedId: string) => deflateSharedPartCompId(inflatedId))

			return originalFn(deflatedIds, ...rest)
		}
	},
	'variants.setVariantForComp': deflateFirstArgument,
	'motion.play': deflateFirstArgument,
	'renderFlags.setRenderFlag': (originalFn: Function): Function => {
		return (flagName: string, renderFlagValue: { [compId: string]: string | null }) => {
			// maybe also 'compsToRenderSticky', 'componentPreviewStates'
			if (flagName === 'componentPreviewStates') {
				const deflatedFlagValue = {}

				Object.keys(renderFlagValue).forEach((compId) => {
					// @ts-ignore
					deflatedFlagValue[deflateSharedPartCompId(compId)] = renderFlagValue[compId]
				})

				originalFn(flagName, deflatedFlagValue)
			} else {
				originalFn(flagName, renderFlagValue)
			}
		}
	},
	'actionsAndBehaviors.handleBehavior': (originalFn: Function): Function => {
		return (...args: Array<any>) => {
			const [behavior, ...rest] = args

			return originalFn(
				{
					...behavior,
					targetId: deflateSharedPartCompId(behavior.targetId),
				},
				...rest
			)
		}
	},
}
